import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { COLORS } from "../lib/colors";
import SetupPage from "./SetupPage";
import DashboardPage from "./DashboardPage";
import FacultyUpload from "./FacultyUpload";
import StudentUpload from "./StudentUpload";
import AlumniUpload from "./AlumniUpload";
import ProgrammeAnalysis from "./ProgrammeAnalysis";
import DocumentUpload from "./DocumentUpload";

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: "\u{1F4CA}" },
  { key: "programme", label: "Programme", icon: "\u{1F4C4}" },
  { key: "documents", label: "Documents", icon: "\u{1F4C2}" },
  { key: "faculty", label: "Faculty CVs", icon: "\u{1F468}\u200D\u{1F3EB}" },
  { key: "students", label: "Student CVs", icon: "\u{1F468}\u200D\u{1F393}" },
  { key: "alumni", label: "Alumni CVs", icon: "\u{1F3AF}" },
  { key: "setup", label: "Setup", icon: "\u2699\uFE0F" },
];

export default function AppShell() {
  const { profile, signOut } = useAuth();
  const [page, setPage] = useState("setup");
  const [institutionId, setInstitutionId] = useState(null);
  const [programmeId, setProgrammeId] = useState(null);
  const [institutionName, setInstitutionName] = useState("");

  const isStaging = import.meta.env.MODE === "staging";

  const handleSetupComplete = (instId, progId, instName) => {
    setInstitutionId(instId);
    setProgrammeId(progId);
    setInstitutionName(instName || "");
    setPage("dashboard");
  };

  const renderPage = () => {
    switch (page) {
      case "setup":
        return <SetupPage onComplete={handleSetupComplete} />;
      case "dashboard":
        return <DashboardPage institutionId={institutionId} programmeId={programmeId} />;
      case "programme":
        return <ProgrammeAnalysis institutionId={institutionId} programmeId={programmeId} />;
      case "documents":
        return <DocumentUpload institutionId={institutionId} />;
      case "faculty":
        return <FacultyUpload institutionId={institutionId} />;
      case "students":
        return <StudentUpload programmeId={programmeId} />;
      case "alumni":
        return <AlumniUpload programmeId={programmeId} />;
      default:
        return <DashboardPage institutionId={institutionId} programmeId={programmeId} />;
    }
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg,
      minHeight: "100vh",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {isStaging && (
        <div style={{
          backgroundColor: COLORS.orange, color: "#fff",
          textAlign: "center", padding: "4px 0", fontSize: 12, fontWeight: 600,
        }}>STAGING ENVIRONMENT</div>
      )}

      {/* Header */}
      <div style={{
        backgroundColor: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, backgroundColor: COLORS.accent,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 16,
          }}>C</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Calibre</div>
            {institutionName && (
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{institutionName}</div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{profile?.email}</span>
          <button
            onClick={signOut}
            style={{
              padding: "6px 14px", fontSize: 13, fontWeight: 500,
              backgroundColor: COLORS.surfaceAlt, color: COLORS.textSecondary,
              border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer",
            }}
          >Sign out</button>
        </div>
      </div>

      {/* Navigation tabs */}
      <div style={{
        backgroundColor: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "0 24px",
        display: "flex",
        gap: 4,
        overflowX: "auto",
      }}>
        {NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            onClick={() => setPage(item.key)}
            style={{
              padding: "12px 16px",
              fontSize: 13,
              fontWeight: page === item.key ? 600 : 400,
              color: page === item.key ? COLORS.accent : COLORS.textSecondary,
              backgroundColor: "transparent",
              border: "none",
              borderBottom: page === item.key ? `2px solid ${COLORS.accent}` : "2px solid transparent",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      {/* Page content */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: 24 }}>
        {renderPage()}
      </div>
    </div>
  );
}
